<template lang="pug">
  .record-payment
    y-dialog(fill-height fluid v-model="showModal" scrollable :fullscreen="$vuetify.breakpoint.xsOnly" transition="fade-transition" persistent max-width="600px")
      v-card(min-height="80vh")
        v-card-title.px-5(:style="`background-color: ${$vuetify.theme.themes[$theme].infoT08}`")
          v-row(no-gutters align="center")
            v-col.shrink
              v-btn.ml-n3(text icon @click="showModal = !showModal" height)
                eva-icon(name="arrow-back-outline" :fill="$vuetify.theme.themes[$theme].basic800" width="24" height="24")
            v-col.ml-3
              p.mb-0.h6.basic800--text {{ income.quotation ? 'Update Quotation' : 'Record Payment' }}
        
        v-card-text.px-0.pb-0
          v-form(ref="form")
            .action-selection.px-6.mt-6
              v-row.px-3.mb-n2.mt-n3(v-if="income.quotation")
                v-radio-group.mt-2(v-model='form.actionSelection' row dense)
                  v-radio.mb-4(value='payment' style="width:100%")
                    template(v-slot:label)
                      div
                        .p1.textColorDefault--text Convert to Invoice & Record as Income
                  v-radio.mb-4(value='convert' style="width:100%")
                    template(v-slot:label)
                      div
                        .p1.textColorDefault--text Convert to Invoice Only
              
            .action-payment(v-if="form.actionSelection == 'payment'")
              .payment-date.px-6
                .label.basic600--text.text-uppercase Date
                v-dialog(v-model='form.date.show' width='290px')
                  template(v-slot:activator='{ on }')
                    v-text-field.mt-2(:value="form.date.value" placeholder="Select date" outlined dense background-color="basic200" readonly v-on='on' :rules="rules.is_selected")
                      template(v-slot:append)
                        eva-icon(name="chevron-down-outline" width="24" height="24" :fill="$vuetify.theme.themes[$theme].basic700")
                  v-date-picker(v-model='form.paymentDate' color="primary" no-title @change="saveDate()")
        
              .payment-method.px-6
                .label.basic600--text.text-uppercase Payment Method
                v-row.px-3.mb-n2.mt-2
                  v-radio-group.mt-2(v-model='form.paymentMethod' row dense)
                    v-radio.mb-4(v-for="(method, index) in paymentMethods" :value="method.name" style="width:100%" :key="method.id")
                      template(v-slot:label)
                        div
                          .p1.textColorDefault--text {{ method.name }}
                
              .payment-amount.px-6
                .label.basic600--text.text-uppercase Amount paid
                v-text-field.mt-2(v-model='form.paymentAmount' :placeholder="$t('price.placeholder')" background-color="basic200" height="40" outlined dense type="number" :rules="priceRules" :prefix="$currency.symbol")
            
            .payment-due-date.px-6.mb-4(v-if="!income.due_date && (form.actionSelection === 'convert' || !isFullPayment)")
              .label.basic600--text.text-uppercase Payment due
              v-dialog(v-model='form.date.show' width='290px')
                v-date-picker(v-model='form.paymentDueDate' color="primary" no-title @change="saveDueDate()")
              v-select.mt-2(v-model="form.paymentDueDate" background-color="basic200" outlined dense hide-no-data hide-details :items="dueDateChoices" item-text="name" item-value="id")
                template(v-slot:append)
                  eva-icon(name="chevron-down-outline" width="24" height="24" :fill="$vuetify.theme.themes[$theme].basic700")
            
            .outstanding-amount.px-6(v-if="form.actionSelection ==='payment'")
              div.d-flex
                .s1.primary500--text Outstanding balance
                v-spacer
                .p1.primary500--text.my-auto {{ outstandingAmount | displayPrice }}
        
        v-card-actions
          v-spacer
          v-btn(color='white', text @click='showModal = false')
            .button-Aa-medium.primary400--text Cancel
          v-btn.mr-2(color='info500' depressed @click="clickSave()" :loading="isLoading")
            .button-AA-medium.basic100--text {{ $t('save') }}
        

</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { DateTime } from 'luxon'

export default {
  name: 'RecordPaymentDialog',
  props: ['open', 'parentIncome'],
  data: () => ({
    isFetching: false,
    form: { 
      actionSelection: 'payment',
      paymentDueDate: null,
      paymentStatus: 'paid',
      paymentMethod: null,
      paymentDate: null,
      paymentAmount: 0,
      date: {
        show: false,
        value: null
      },
      dueDate: {
        show: false,
        value: null
      }
    },
    paymentMethods: [],
    dueDateChoices: [],
    originalDateChoices: [],
    isLoading: false,
    rules: {
      is_selected: [(v) => v !== null || 'This field is required'],
      has_quantity: [(v) => !!v || 'This field is required', (v) => v > 0|| 'This field is required']
    }
  }),
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
    showModal: {
      get() {
        return this.open
      },
      set(newValue) {
        this.$emit('update:open', newValue)
      }
    },
    income: {
      get() {
        return this.parentIncome
      },
      set(newValue) {
        this.$emit('update:parentIncome', newValue)
      }
    },
    amountDue () {
      return this.income.amount_remaining
    },
    isFullPayment () {
      if (this.paymentAmount === this.income.amount.toString()) {
        return true
      }
      return false
    },
    paymentAmount () {
      return (parseFloat(this.form.paymentAmount)).toFixed(this.$currency.exponent).replace('.', '')
    },
    outstandingAmount () {
      if (this.form.paymentAmount) {
        const total = this.income.amount_remaining - this.form.paymentAmount*this.$currency.multiplier
        return total >= 0 ? total : 0
      }
      return this.income.amount_remaining
    },
    priceRules () {
      return [
        v => !!v || 'Amount is required',
        v => v != 0|| 'Amount is required',
        v => v <= this.amountDue/this.$currency.multiplier || `Amount should not be above ${this.$currency.symbol} ${(parseFloat(this.amountDue/this.$currency.multiplier)).toFixed(this.$currency.exponent)}`,
      ]
    }
  },
  mounted () {
    this.form.paymentDate = DateTime.local().toFormat('yyyy-MM-dd')
    this.form.date.value = this.formatDate(this.form.paymentDate)
    this.getPaymentMethods()
    this.form.paymentAmount = this.income.amount_remaining/this.$currency.multiplier
    this.form.paymentDueDate = DateTime.local().toFormat('yyyy-MM-dd')
    const selectedDate = DateTime.fromFormat(this.form.paymentDueDate, 'yyyy-MM-dd')
    this.originalDateChoices = [
      { name: `Due ${selectedDate.toFormat('dd/MM/yyyy')}`, id: selectedDate.toFormat('yyyy-MM-dd') },
      { name: 'Within 15 days', id: selectedDate.plus({ days: 15 }).toFormat('yyyy-MM-dd') },
      { name: 'Within 30 days', id: selectedDate.plus({ days: 30 }).toFormat('yyyy-MM-dd') },
      { name: 'Within 45 days', id: selectedDate.plus({ days: 45 }).toFormat('yyyy-MM-dd') },
      { name: 'Within 60 days', id: selectedDate.plus({ days: 60 }).toFormat('yyyy-MM-dd') },
      { name: 'Within 90 days', id: selectedDate.plus({ days: 90 }).toFormat('yyyy-MM-dd') },
      { name: 'Custom', id: 'custom'}
    ]
    this.dueDateChoices = [ ...this.originalDateChoices ]
  },
  methods: {
    ...mapActions([
      'showMessage'
    ]),
    getPaymentMethods () {
      this.isFetching = true
      this.$axios.get(`/books/${this.company.slug}/payment_methods`).then((response) => {
        this.isFetching = false
        this.paymentMethods = [...response.data.results]
        this.form.paymentMethod = response.data.results.filter(method => method.name.toLowerCase() === 'bank transfer')[0]
        if (!this.form.paymentMethod) {
          this.form.paymentMethod = this.paymentMethods[0].name
        } else {
          this.form.paymentMethod = this.form.paymentMethod.name
        } 
      }).catch(() => {})
    },
    saveDate () {
      this.form.date.show = false
      this.form.date.value = this.formatDate(this.form.paymentDate)
    },
    saveDueDate () {
      this.form.dueDate.show = false
      this.form.dueDate.value = this.formatDate(this.form.paymentDueDate)
    },
    formatDate (date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      return `${day}/${month}/${year}`
    },
    clickSave() {
      if (!this.$refs.form.validate()) {
        return
      }
      this.isLoading = true
      const data = { quotation: false }
      if (!this.income.due_date) {
        data.due_date = this.form.paymentDueDate
      }
      this.$axios.patch(`/books/${this.company.slug}/incomes/${this.income.pid}/`, data)
        .then(resp => {
          if (this.form.actionSelection === 'convert') {
            this.income = resp.data
            this.showModal = false
          }
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
      if (this.form.actionSelection === 'convert') {
        return
      }
      const params = {
        date: this.form.paymentDate,
        payment_method: this.form.paymentMethod,
        amount: this.paymentAmount
      }
      let paymentPath = 'add_partial_payment'
      if (this.isFullPayment) {
        paymentPath = 'add_full_payment'
      }
      this.$axios.post(`/books/${this.company.slug}/incomes/${this.income.pid}/${paymentPath}/`, params)
        .then(resp => {
          this.$emit('payment-added', resp.data)
          this.$axios.get(`/books/${this.company.slug}/incomes/${this.income.pid}/?expand=category,products`)
            .then(resp => {
              this.showMessage(['success', this.$t('success_message.income_recorded_successfully')])
              this.income = resp.data
              this.showModal = false
            })
            .catch(() => {})
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          this.isLoading = false
        })
    }
  }
}
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-primary200);
}
.error--text >>> fieldset {
  border-color: var(--v-danger);
}
</style>

<i18n>
{
  "en": {
    "price": {
      "label": "Amount",
      "placeholder": "0"
    },
    "cancel": "Cancel",
    "save": "Save"
  }
}
</i18n>
